<template>
  <v-dialog v-model="internalIsVisible" max-width="600px">
    <v-card>
      <v-card-title>Modificar {{ nombreEvento(props.torneo?.formatoTorneo).toLowerCase() }}</v-card-title>

      <v-divider></v-divider>

      <v-card-text>
        <v-form ref="form" v-model="isValid" lazy-validation>
          <!-- Nombre del torneo -->
          <v-text-field
            v-model="nombreTorneo"
            :label="`Nombre ${textos.del}`"
            required
            type="text"
            :rules="[(v: string) => !!v || 'Campo obligatorio']"
          ></v-text-field>

          <!-- Descripción del torneo -->
          <v-textarea
            v-model="descripcionTorneo"
            :label="`Descripción ${textos.del} (opcional)`"
            type="text"
            clearable
          ></v-textarea>

          <!-- Lugar del torneo -->
          <!-- Campo de búsqueda de dirección -->
          <v-text-field
            v-model="searchQuery"
            :label="`¿Dónde se celebra ${textos.el}?`"
            placeholder="Ej: Calle Gran Vía, Madrid"
            @keyup.enter="searchLocation"
          >
            <template v-slot:append>
              <v-btn
                :loading="isSearching"
                color="primary"
                variant="tonal"
                @click="searchLocation"
                >Buscar</v-btn
              >
            </template>
          </v-text-field>

          <!-- Mensaje de error -->
          <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
          <!-- Muestra la dirección formateada si es válida -->
          <p v-else-if="formattedAddress">
            Dirección validada: {{ formattedAddress }}
          </p>

          <!-- Mapa solo para visualización (NO interactivo) -->
          <l-map
            v-if="selectedLocation"
            :zoom="zoom"
            :center="[selectedLocation.lat, selectedLocation.lng]"
            :options="{
              dragging: false,
              zoomControl: false,
              scrollWheelZoom: false,
            }"
            style="height: 300px; width: 100%"
          >
            <l-tile-layer :url="tileLayerUrl"></l-tile-layer>
            <l-marker :lat-lng="selectedLocation">
              <l-popup>{{ formattedAddress }}</l-popup>
            </l-marker>
          </l-map>

          <!-- Límite de participantes -->
          <v-text-field
            v-model="limiteParticipantes"
            :label="etiquetaLimitePlazas(tipoTorneo)"
            required
            type="number"
            :rules="[
              (v: number) => !isNaN(v) || 'Debe ser un número',
              (v: number) => v > 0 || 'Debe ser mayor que 0',
            ]"
          ></v-text-field>

          <!-- puntosTorneo -->
          <v-text-field
            v-model="puntosTorneo"
            :label="`¿A cuántos puntos es ${textos.el}?`"
            required
            type="number"
            :rules="[
              (v: number) => !isNaN(v) || 'Debe ser un número',
              (v: number) => v > 0 || 'Debe ser mayor que 0',
            ]"
          ></v-text-field>

          <!-- Opciones de liga y copa: se pueden cambiar mientras no haya jornadas -->
          <template v-if="porJornadas">
            <v-select
              v-model="cuentaElo"
              label="¿Cuenta para el ELO?"
              :items="cuentaEloOptions"
              item-title="label"
              item-value="value"
              hint="Con las jornadas generadas no se puede cambiar"
              persistent-hint
            />
            <v-select
              v-model="idaYVuelta"
              :label="esCopa ? '¿Grupos a solo ida o ida y vuelta?' : '¿Solo ida o ida y vuelta?'"
              :items="idaYVueltaOptions"
              item-title="label"
              item-value="value"
              hint="Con las jornadas generadas no se puede cambiar"
              persistent-hint
              class="mt-2"
            />
          </template>

          <!-- Opciones de la copa: grupos y reparto hasta generar las jornadas; el resto, hasta la eliminatoria -->
          <template v-if="esCopa">
            <v-text-field
              v-model.number="numeroGrupos"
              type="number"
              label="¿Cuántos grupos?"
              hint="Con las jornadas generadas no se puede cambiar"
              persistent-hint
              class="mt-2"
              :rules="[(v: number) => v >= 1 || 'Al menos un grupo']"
            />
            <v-select
              v-model="repartoGrupos"
              label="¿Cómo se reparten los grupos?"
              :items="repartoGruposOptions"
              item-title="label"
              item-value="value"
              hint="Con las jornadas generadas no se puede cambiar"
              persistent-hint
              class="mt-2"
            />
            <v-text-field
              v-model.number="clasificadosPorGrupo"
              type="number"
              label="¿Cuántos pasan de cada grupo?"
              hint="Con la eliminatoria generada no se puede cambiar"
              persistent-hint
              class="mt-2"
              :rules="[(v: number) => v >= 1 || 'Al menos uno']"
            />
            <v-select
              v-model="desempateEliminatoria"
              label="Empate en la eliminatoria"
              :items="desempateEliminatoriaOptions"
              item-title="label"
              item-value="value"
              hint="Con la eliminatoria generada no se puede cambiar"
              persistent-hint
              class="mt-2"
            />
            <v-select
              v-model="tercerPuesto"
              label="¿Tercer puesto?"
              :items="tercerPuestoOptions"
              item-title="label"
              item-value="value"
              hint="Con la eliminatoria generada no se puede cambiar"
              persistent-hint
              class="mt-2"
            />
          </template>

          <!-- listasPorJugador: en Liga una -->
          <v-text-field
            v-if="!porJornadas"
            v-model="listasPorJugador"
            label="¿Cúantas listas necesitará un jugador?"
            required
            type="number"
            :disabled="esListaConjunta"
            :hint="esListaConjunta ? 'Con lista conjunta cada pareja entrega una' : ''"
            persistent-hint
            :rules="[
              (v: number) => !isNaN(v) || 'Debe ser un número',
              (v: number) => v > 0 || 'Debe ser mayor que 0',
            ]"
          ></v-text-field>

          <!-- Numero de rondas: en Liga son las jornadas generadas -->
          <v-text-field
            v-if="!porJornadas"
            v-model="numeroPartidas"
            label="¿Cuantas rondas tiene el torneo?"
            :disabled="cerrado"
            required
            type="number"
            :rules="[
              (v: number) => !isNaN(v) || 'Debe ser un número',
              (v: number) => v > 0 || 'Debe ser mayor que 0',
            ]"
          ></v-text-field>

          <!-- Tipo de torneo: la Liga es individual -->
          <v-select
            v-if="!porJornadas"
            v-model="tipoTorneo"
            label="¿De qué tipo es el torneo?"
            :disabled="cerrado"
            required
            :items="tipoTorneoOptions"
            item-title="label"
            item-value="value"
            :rules="[(v: string) => !!v || 'Campo obligatorio']"
          >
            <template #item="{ item, props }">
              <v-list-item v-bind="props" :subtitle="item.raw.subtitle" />
            </template>
          </v-select>

          <!-- Modo de listas, solo en Parejas -->
          <v-select
            v-if="tipoTorneo === 'Parejas' && !porJornadas"
            v-model="listaPorPareja"
            label="¿Cómo entregan las listas las parejas?"
            :disabled="cerrado"
            :items="modoListasParejaOptions"
            item-title="label"
            item-value="value"
          />

          <!-- Tipo de clasificación -->
          <v-select
            v-model="classificationType"
            label="¿Qué tipo de clasificación se utilizará?"
            :disabled="cerrado"
            :hint="cerrado ? 'Con los resultados guardados ya no cambia cómo puntúa.' : undefined"
            :persistent-hint="cerrado"
            required
            :items="classificationTypeOptions"
            item-title="label"
            item-value="value"
            :rules="[(v: number) => !!v || 'Campo obligatorio']"
          >
            <template #item="{ item, props }">
              <v-list-item v-bind="props" :subtitle="item.raw.subtitle" />
            </template>
          </v-select>
          <!-- Cómo puntúa el tipo elegido -->
          <v-alert
            v-if="classificationType"
            type="info"
            variant="tonal"
            density="compact"
            class="mb-4 text-left text-body-2"
          >
            {{ ClassificationTypeDescriptions[classificationType].detalle }}
            {{ DESEMPATE_CLASIFICACION }}
          </v-alert>

          <!-- fecha de inicio -->
          <v-text-field
            v-model="fechaInicioTorneo"
            :label="`Inicio ${textos.del}`"
            type="date"
            :rules="[
              validateRequired,
              validateNotPast,
              validateFechaInicioFinTorneo,
            ]"
            required
            @dateChanged="handleDateChangeInicio"
            :error-messages="errorFechaTorneo"
          ></v-text-field>

          <!-- Fecha de fin del torneo -->
          <v-text-field
            v-model="fechaFinTorneo"
            :label="`Fin ${textos.del}`"
            type="date"
            :rules="[validateRequired, validateFechaFinInicioTorneo]"
            required
            @dateChanged="handleDateChangeFin"
            :error-messages="errorFechaTorneo"
          ></v-text-field>

          <!-- Precio del torneo -->
          <v-text-field
            v-model="precioTorneo"
            :label="`¿Cuál es el precio ${textos.del}?`"
            required
            type="number"
            :rules="[(v: number) => !isNaN(v) || 'Debe ser un número']"
          ></v-text-field>

          <!-- metodosPago -->
          <v-textarea
            v-model="metodosPago"
            label="¿Como se puede pagar?"
            type="text"
            clearable
          ></v-textarea>

          <!-- Inicio de las inscripciones -->
          <v-text-field
            v-model="fechaInicioInscripcion"
            label="Inicio de las inscripciones"
            type="date"
            :rules="[
              validateRequired,
              validateAntesDe(
                fechaFinInscripcion,
                'Debe ser antes de la fecha de fin de inscripción'
              ),
              validateAntesDe(
                fechaInicioTorneo,
                `Debe ser antes de la fecha de inicio ${textos.del}`
              ),
            ]"
            required
            @dateChanged="handleDateChangeInicioInscripcion"
            :error-messages="errorFechaInscripcion"
          ></v-text-field>

          <!-- Último día para apuntarse -->
          <v-text-field
            v-model="fechaFinInscripcion"
            :label="`Último día para apuntarse ${textos.al}`"
            type="date"
            :rules="[
              validateRequired,
              validateFinDespuesDeInicio(
                fechaInicioInscripcion,
                'Debe ser posterior al inicio de inscripción'
              ),
              validateAntesDe(
                fechaInicioTorneo,
                `Debe ser antes del inicio ${textos.del}`
              ),
            ]"
            required
            @dateChanged="handleDateChangeInscripcion"
            :error-messages="
              [errorFechaInscripcion, errorFechaCierreInscripcion].filter(
                Boolean
              )
            "
          ></v-text-field>

          <!-- Último día para entregar las listas -->
          <v-text-field
            v-model="fechaFinEntregaListas"
            :label="
              porJornadas
                ? 'Último día para entregar la primera lista'
                : 'Último día para entregar las listas'
            "
            type="date"
            :rules="[
              validateRequired,
              validateAntesDe(
                fechaInicioTorneo,
                `Debe ser antes del inicio ${textos.del}`
              ),
            ]"
            required
            @dateChanged="handleDateChangeListas"
            :error-messages="errorFechaListas"
          ></v-text-field>

          <!-- bases del torneo -->
          <v-file-input
            clearable
            :label="`Bases ${textos.del}`"
            required
            accept="application/pdf"
            @change="onFileChange"
            show-size
          ></v-file-input>

          <!-- cartel del torneo -->
          <v-file-input
            ref="imageInput"
            label="Selecciona una imagen"
            @change="onImageSelected"
            accept="image/*"
            show-size
          ></v-file-input>

          <!-- hora del comienza del torneo: la liga no tiene horario -->
          <v-text-field
            v-if="!porJornadas"
            v-model="horaInicioTorneo"
            label="Hora de inicio del torneo (HH:MM)"
            clearable
            :rules="[
              (v: any) =>
                /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(v) ||
                'Formato debe ser HH:MM',
            ]"
          ></v-text-field>

          <!-- hora a la que acaba del torneo -->
          <v-text-field
            v-if="!porJornadas"
            v-model="horaFinTorneo"
            label="Hora de finalización del torneo (HH:MM)"
            clearable
            :rules="[
              (v: any) =>
                /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(v) ||
                'Formato debe ser HH:MM',
            ]"
          ></v-text-field>

          <!-- Contacto (opcional) -->
          <v-text-field
            v-model="contacto"
            label="Contacto (email o teléfono, opcional)"
            hint="El contacto será visible para los usuarios registrados si se rellena"
            persistent-hint
            clearable
            type="text"
          ></v-text-field>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-row justify="center" class="my-4 ga-5">
          <v-btn
            :disabled="isGenerating"
            @click="confirmarConfiguracion"
            color="primary"
            variant="tonal"
            >Modificar</v-btn
          >
          <v-btn variant="tonal" color="secondary" @click="closeModal" large
            >Cancelar</v-btn
          >
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Modal de progreso circular -->
  <v-dialog v-model="isGenerating" hide-overlay persistent>
    <v-card class="progress-card">
      <v-progress-circular
        indeterminate
        color="primary"
        size="70"
      ></v-progress-circular>
      <div class="text-center">Guardando cambios...</div>
    </v-card>
  </v-dialog>

  <!-- Modal response eliminar inscripcion -->
  <ModalSuccess
    :isVisible="showSuccessModal"
    :message="`${nombreEvento(props.torneo?.formatoTorneo)} ${esLiga || esCopa ? 'modificada' : 'modificado'} correctamente.`"
    @update:isVisible="showSuccessModal = $event"
  />

  <!-- Modal response si error -->
  <ModalError
    :isVisible="showErrorModal"
    :message="mensajeError"
    @update:isVisible="showErrorModal = $event"
  />
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import ModalSuccess from "../Commons/ModalSuccess.vue";
import ModalError from "../Commons/ModalError.vue";
import { ModificarTorneoDTO, Torneo } from "@/interfaces/Torneo";
import { modificarTorneo } from "@/services/TorneosService";
import { useTorneoCerrado } from "@/composables/useTorneoCerrado";
import { mensajeDelBack } from "@/utils/mensajeDelBack";
import { LMap, LTileLayer, LMarker } from "@vue-leaflet/vue-leaflet";
import axios from "axios";
import {
  modoListasParejaOptions,
  tipoTorneoOptions,
  torneoType,
  TorneoType,
  etiquetaLimitePlazas,
} from "@/Constant/TiposTorneo";
import {
  ClassificationType,
  ClassificationTypeDescriptions,
  ClassificationTypeLabels,
  DESEMPATE_CLASIFICACION,
} from "@/Constant/TipoClasificacion";
import {
  FORMATO_COPA,
  FORMATO_LIGA,
  cuentaEloOptions,
  desempateEliminatoriaOptions,
  esPorJornadas,
  idaYVueltaOptions,
  nombreEvento,
  repartoGruposOptions,
  tercerPuestoOptions,
  textosEvento,
} from "@/Constant/FormatoTorneo";

// eslint-disable-next-line no-undef
const props = defineProps<{
  isVisible: boolean;
  torneo: Torneo | undefined;
}>();

// eslint-disable-next-line no-undef
const emit = defineEmits(["close", "confirm"]);

const internalIsVisible = ref(props.isVisible);
const isValid = ref(true);

const classificationTypeOptions = [
  ClassificationType.NORMAL,
  ClassificationType.EXTENDED,
  ClassificationType.ALEMAN,
].map(value => ({
  label: ClassificationTypeLabels[value],
  subtitle: ClassificationTypeDescriptions[value].resumen,
  value,
}));

watch(
  () => props.isVisible,
  newValue => {
    internalIsVisible.value = newValue;
  }
);

const searchQuery = ref<string>("");
const formattedAddress = ref<string>("");
const selectedLocation = ref<{ lat: number; lng: number } | null>(null);
const errorMessage = ref<string>("");
const zoom = ref<number>(14);
const tileLayerUrl = ref<string>(
  "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
);

const isSearching = ref<boolean>(false);
// Función para buscar la dirección ingresada por el usuario
const searchLocation = async () => {
  if (!searchQuery.value.trim()) return;
  isSearching.value = true;

  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
    searchQuery.value
  )}&addressdetails=1`;

  try {
    const response = await axios.get(url);
    if (response.data.length > 0) {
      const firstResult = response.data[0];
      selectedLocation.value = {
        lat: parseFloat(firstResult.lat),
        lng: parseFloat(firstResult.lon),
      };
      lugarTorneo.value = firstResult.lat + ", " + firstResult.lon;
      formattedAddress.value = formatAddress(firstResult);
      errorMessage.value = "";
    } else {
      errorMessage.value =
        "No se encontró la dirección. Se guardará sin enlace a Google Maps.";
      selectedLocation.value = null;
      lugarTorneo.value = searchQuery.value;
      formattedAddress.value = searchQuery.value;
    }
  } catch (error) {
    console.error("Error buscando la dirección:", error);
    errorMessage.value = "Error al buscar la dirección.";
  } finally {
    isSearching.value = false;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formatAddress = (data: any): string => {
  if (!data || !data.display_name) return "Dirección no disponible";

  const { name, address } = data;

  // Extraer componentes clave
  const road = address.road || name || "";
  const houseNumber = address.house_number ? `, ${address.house_number}` : "";
  const city = address.city || address.town || address.village || "";
  const state = address.state || "";
  const postcode = address.postcode || "";
  const country = address.country || "";

  // Construir dirección con formato legible
  return [`${road}${houseNumber}`, city, state, postcode, country]
    .filter(Boolean) // Elimina valores vacíos
    .join(", ");
};

// Al abrir, el lugar guardado en el buscador y en el mapa: son coordenadas "lat, lon" o, si no se encontró
// la dirección al crearlo, el texto tal cual
const mostrarLugarGuardado = async (lugar: string) => {
  const coordenadas = lugar.match(/^(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)$/);
  if (!coordenadas) {
    searchQuery.value = lugar;
    formattedAddress.value = "";
    selectedLocation.value = null;
    return;
  }
  const lat = parseFloat(coordenadas[1]);
  const lng = parseFloat(coordenadas[2]);
  selectedLocation.value = { lat, lng };
  try {
    const { data } = await axios.get(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
    );
    formattedAddress.value = data?.display_name ? formatAddress(data) : lugar;
  } catch (error) {
    console.error("Error obteniendo la dirección:", error);
    formattedAddress.value = lugar;
  }
  searchQuery.value = formattedAddress.value;
};

const handleDateChangeInicio = (newDate: string | null) => {
  if (newDate) fechaInicioTorneo.value = convertirFecha(newDate);
};
const handleDateChangeFin = (newDate: string | null) => {
  if (newDate) fechaFinTorneo.value = convertirFecha(newDate);
};
const handleDateChangeInscripcion = (newDate: string | null) => {
  if (newDate) fechaFinInscripcion.value = convertirFecha(newDate);
};
const handleDateChangeListas = (newDate: string | null) => {
  if (newDate) fechaFinEntregaListas.value = convertirFecha(newDate);
};
const handleDateChangeInicioInscripcion = (newDate: string | null) => {
  if (newDate) fechaInicioInscripcion.value = convertirFecha(newDate);
};
const nombreTorneo = ref<string>();
const limiteParticipantes = ref<number>();
const descripcionTorneo = ref<string>();
const precioTorneo = ref<number>();
const numeroPartidas = ref<number>();
const puntosTorneo = ref<number>();
const estadoTorneo = ref<number>(1);
const lugarTorneo = ref<string>();
const tipoTorneo = ref<string>("");
const listaPorPareja = ref<boolean>(false);
const esListaConjunta = computed(
  () => tipoTorneo.value === "Parejas" && listaPorPareja.value
);
// El formato no se cambia al modificar: solo sus opciones, en Liga
const esLiga = computed(() => props.torneo?.formatoTorneo === FORMATO_LIGA);
const esCopa = computed(() => props.torneo?.formatoTorneo === FORMATO_COPA);
const porJornadas = computed(() => esPorJornadas(props.torneo?.formatoTorneo));
// Los textos del formulario hablan del tipo de evento
const textos = computed(() => textosEvento(props.torneo?.formatoTorneo));
// Solo en Copa
const numeroGrupos = ref<number>(2);
const clasificadosPorGrupo = ref<number>(2);
const repartoGrupos = ref<string>("Elo");
const desempateEliminatoria = ref<string>("Grupos");
const tercerPuesto = ref<boolean>(false);
const cuentaElo = ref<boolean>(false);
const idaYVuelta = ref<boolean>(false);
const classificationType = ref<ClassificationType>();
// Cerrado, lo que decide cómo puntúa ya no se cambia: la clasificación dejaría de ser la de las posiciones guardadas
const cerrado = useTorneoCerrado();
const idRangoTorneo = ref<number>(1);
const esMatchedPlayTorneo = ref<boolean>(false);
const metodosPago = ref<string[]>([]);
const horaInicioTorneo = ref<string>("");
const horaFinTorneo = ref<string>("");
const contacto = ref<string | undefined>(undefined);
const fechaInicioInscripcion = ref<string>("");
const fechaInicioTorneo = ref<string>("");
const fechaFinTorneo = ref<string>("");
const fechaFinEntregaListas = ref<string>("");
const fechaFinInscripcion = ref<string>("");
const imageBase64 = ref<string | null>(null);
const byteArrayBases = ref<Uint8Array | null>(null);
const isGenerating = ref<boolean>(false);
const showErrorModal = ref<boolean>(false);
const mensajeError = ref("");
const showSuccessModal = ref<boolean>(false);
const listasPorJugador = ref<number>();
// Con lista conjunta cada pareja entrega una sola lista
watch(esListaConjunta, conjunta => {
  if (conjunta) listasPorJugador.value = 1;
});
const form = ref();

watch(
  () => props.torneo,
  newTorneo => {
    if (newTorneo) {
      nombreTorneo.value = newTorneo.nombreTorneo || "";
      limiteParticipantes.value = newTorneo.limiteParticipantes;
      descripcionTorneo.value = newTorneo.descripcionTorneo || "";
      lugarTorneo.value = newTorneo.lugarTorneo || "";
      mostrarLugarGuardado(lugarTorneo.value);
      precioTorneo.value = newTorneo.precioTorneo;
      numeroPartidas.value = newTorneo.numeroPartidas;
      puntosTorneo.value = newTorneo.puntosTorneo;
      metodosPago.value = newTorneo.metodosPago;
      horaInicioTorneo.value =
        newTorneo.horaInicioTorneo.substring(
          0,
          newTorneo.horaInicioTorneo.lastIndexOf(":")
        ) || "00:00";
      horaFinTorneo.value =
        newTorneo.horaFinTorneo.substring(
          0,
          newTorneo.horaFinTorneo.lastIndexOf(":")
        ) || "00:00";
      fechaInicioTorneo.value = newTorneo.fechaInicioTorneo;
      fechaFinTorneo.value = newTorneo.fechaFinTorneo;
      fechaFinEntregaListas.value = newTorneo.fechaEntregaListas;
      fechaFinInscripcion.value = newTorneo.fechaFinInscripcion;
      fechaInicioInscripcion.value = newTorneo.inicioInscripciones;
      listasPorJugador.value = newTorneo.listasPorJugador;
      tipoTorneo.value = newTorneo.tipoTorneo;
      listaPorPareja.value = newTorneo.listaPorPareja ?? false;
      classificationType.value = newTorneo.classificationType;
      contacto.value = newTorneo.contacto || undefined;
      cuentaElo.value = newTorneo.cuentaElo ?? false;
      idaYVuelta.value = newTorneo.idaYVuelta ?? false;
      numeroGrupos.value = newTorneo.numeroGrupos ?? 2;
      clasificadosPorGrupo.value = newTorneo.clasificadosPorGrupo ?? 2;
      repartoGrupos.value = newTorneo.repartoGrupos ?? "Elo";
      desempateEliminatoria.value = newTorneo.desempateEliminatoria ?? "Grupos";
      tercerPuesto.value = newTorneo.tercerPuesto ?? false;
    }
  },
  { immediate: true }
);

const closeModal = () => {
  internalIsVisible.value = false;
  emit("close");
};

const errorFechaTorneo = ref<string>("");
const errorFechaInscripcion = ref<string>("");
const errorFechaCierreInscripcion = ref<string>("");
const errorFechaListas = ref<string>("");

watch([fechaInicioTorneo, fechaFinTorneo], ([inicio, fin]) => {
  errorFechaTorneo.value =
    inicio && fin && new Date(inicio) > new Date(fin)
      ? "La fecha de comienzo del torneo debe ser anterior a la fecha de finalización."
      : "";
});

watch([fechaInicioInscripcion, fechaFinInscripcion], ([inicio, fin]) => {
  errorFechaInscripcion.value =
    inicio && fin && new Date(inicio) > new Date(fin)
      ? "La fecha de apertura de inscripciones debe ser anterior a la de cierre."
      : "";
});

watch(
  [fechaFinInscripcion, fechaInicioTorneo],
  ([finInscrip, inicioTorneo]) => {
    errorFechaCierreInscripcion.value =
      finInscrip &&
      inicioTorneo &&
      new Date(finInscrip) > new Date(inicioTorneo)
        ? "El cierre de inscripciones debe ser anterior al inicio del torneo."
        : "";
  }
);

watch(
  [fechaFinEntregaListas, fechaInicioTorneo],
  ([finListas, inicioTorneo]) => {
    errorFechaListas.value =
      finListas && inicioTorneo && new Date(finListas) > new Date(inicioTorneo)
        ? "La fecha de cierre de envío de listas debe ser anterior al inicio del torneo."
        : "";
  }
);

const convertirFecha = (dateString: string) => {
  const dateParts = dateString.split("/");

  if (dateParts.length !== 3) {
    throw new Error("El formato de la fecha debe ser dd-mm-aaaa");
  }

  const day = dateParts[0];
  const month = dateParts[1];
  const year = dateParts[2];

  return `${year}-${month}-${day}`;
};

const onImageSelected = async (event: Event) => {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = e => {
      const img = new Image();
      img.onload = () => {
        const maxWidth = 500; // Cambia esto al ancho máximo deseado
        const maxHeight = 500; // Cambia esto a la altura máxima deseada
        let width = img.width;
        let height = img.height;

        if (width > maxWidth || height > maxHeight) {
          if (width > height) {
            height *= maxWidth / width;
            width = maxWidth;
          } else {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
          // Convertir el canvas a base64
          imageBase64.value = canvas.toDataURL("image/jpeg", 1); // Ajusta la calidad de 0 a 1
        }
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }
};

const onFileChange = (event: Event) => {
  const file = (event.target as HTMLInputElement).files?.[0];

  if (file) {
    const reader = new FileReader();

    reader.onload = async event => {
      if (event.target?.result) {
        const arrayBuffer = event.target.result as ArrayBuffer;
        byteArrayBases.value = new Uint8Array(arrayBuffer);
      }
    };

    reader.readAsArrayBuffer(file);
  }
};

const confirmarConfiguracion = async () => {
  const isFormValid = await form.value.validate();
  if (!isFormValid) return;

  if (!isValid.value) {
    return;
  }

  const basesTorneoBase64 = byteArrayBases.value
    ? btoa(
        new Uint8Array(byteArrayBases.value).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          ""
        )
      )
    : null;

  const torneoTypeSelected = computed<TorneoType>(() => {
    return torneoType.includes(tipoTorneo.value as TorneoType)
      ? (tipoTorneo.value as TorneoType)
      : "Individual"; // Valor por defecto
  });

  const nuevoTorneo: ModificarTorneoDTO = {
    nombreTorneo: nombreTorneo.value,
    limiteParticipantes: limiteParticipantes.value,
    fechaInicioTorneo: fechaInicioTorneo.value,
    fechaFinTorneo: fechaFinTorneo.value,
    precioTorneo: precioTorneo.value,
    numeroPartidas: numeroPartidas.value,
    puntosTorneo: puntosTorneo.value,
    estadoTorneo: estadoTorneo.value,
    lugarTorneo: lugarTorneo.value,
    tipoTorneo: torneoTypeSelected.value,
    idRangoTorneo: idRangoTorneo.value,
    esMatchedPlayTorneo: esMatchedPlayTorneo.value,
    fechaEntregaListas: fechaFinEntregaListas.value,
    fechaFinInscripcion: fechaFinInscripcion.value,
    horaInicioTorneo: horaInicioTorneo.value + ":00",
    horaFinTorneo: horaFinTorneo.value + ":00",
    cartelTorneo: imageBase64.value ?? undefined,
    basesTorneo: basesTorneoBase64 ?? undefined,
    descripcionTorneo: descripcionTorneo.value,
    idTorneo: props.torneo?.idTorneo,
    inicioInscripciones: fechaInicioInscripcion.value,
    listasPorJugador: listasPorJugador.value ?? 1,
    listaPorPareja: esListaConjunta.value,
    classificationType: classificationType.value ?? ClassificationType.NORMAL,
    contacto: contacto.value || undefined,
    cuentaElo: porJornadas.value ? cuentaElo.value : undefined,
    idaYVuelta: porJornadas.value ? idaYVuelta.value : undefined,
    // Solo si cambian: el back rechaza cambiar lo que ya no se puede
    ...(esCopa.value && {
      numeroGrupos: numeroGrupos.value !== props.torneo?.numeroGrupos ? numeroGrupos.value : undefined,
      repartoGrupos: repartoGrupos.value !== props.torneo?.repartoGrupos ? repartoGrupos.value : undefined,
      clasificadosPorGrupo:
        clasificadosPorGrupo.value !== props.torneo?.clasificadosPorGrupo ? clasificadosPorGrupo.value : undefined,
      desempateEliminatoria:
        desempateEliminatoria.value !== props.torneo?.desempateEliminatoria ? desempateEliminatoria.value : undefined,
      tercerPuesto: tercerPuesto.value !== props.torneo?.tercerPuesto ? tercerPuesto.value : undefined,
    }),
  };
  try {
    isGenerating.value = true;

    await modificarTorneo(nuevoTorneo);

    showSuccessModal.value = true;
  } catch (error) {
    // El back dice qué no se ha podido cambiar: por ejemplo, un nombre que ya tienen otras bases
    mensajeError.value = mensajeDelBack(
      error,
      `No se ha podido modificar ${textos.value.el}. Intentalo de nuevo y si el error persiste contacta con el administrador.`
    );
    showErrorModal.value = true;
    console.error(error);
  } finally {
    isGenerating.value = false;
  }

  emit("confirm");
  closeModal();
};

const today = new Date().toISOString().split("T")[0];

const validateRequired = (v: string) => !!v || "Campo obligatorio";
const validateNotPast = (v: string) =>
  v >= today || "No puede ser una fecha pasada";
const validateFechaInicioFinTorneo = (v: string) =>
  !fechaFinTorneo.value ||
  v <= fechaFinTorneo.value ||
  "Debe ser anterior o igual a la fecha de fin";
const validateFechaFinInicioTorneo = (v: string) =>
  !fechaInicioTorneo.value ||
  v >= fechaInicioTorneo.value ||
  "Debe ser posterior o igual a la fecha de inicio";

const validateFinDespuesDeInicio =
  (inicio: string, mensaje = "Debe ser posterior o igual al inicio") =>
  (v: string) =>
    !inicio || !v || v >= inicio || mensaje;

const validateAntesDe = (fechaLimite: string, mensaje: string) => (v: string) =>
  !fechaLimite || !v || v <= fechaLimite || mensaje;
</script>
<style scoped></style>
